import { useMemo } from 'react';
import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { Channel } from '@src/models/channel';
import { applicationApi } from '@src/apis/admin';
import type { ApplicationDetailResponseModel, ApplicationMemberResponseModel } from '@uniquegood/realworld-admin-interface';

export interface ChannelDetailWithMembers extends Channel {
  members?: ApplicationMemberResponseModel[] | null;
}

type ChannelDetailQueryKey = readonly ['channel-detail', string];

export interface UseChannelDetailOptions {
    /**
     * React Query 옵션을 덮어씁니다.
     * `queryKey`와 `queryFn`은 내부에서 관리하므로 제외했습니다.
     */
    queryOptions?: Omit<
        UseQueryOptions<ChannelDetailWithMembers, Error, ChannelDetailWithMembers, ChannelDetailQueryKey>,
        'queryKey' | 'queryFn'
    >;
}

/**
 * 채널 상세 조회 훅
 *
 * @description
 * - 채널 ID를 받아 채널 상세 정보를 조회합니다.
 * - ApplicationApi.getApplicationDetail을 사용하여 실제 API를 호출합니다.
 */
export const useChannelDetail = (
    channelId: string,
    options: UseChannelDetailOptions = {}
): UseQueryResult<ChannelDetailWithMembers> => {
    const { queryOptions } = options;
    const { staleTime, ...restQueryOptions } = queryOptions ?? {};

    const queryKey = useMemo<ChannelDetailQueryKey>(
        () => ['channel-detail', channelId] as const,
        [channelId]
    );

    return useQuery<ChannelDetailWithMembers, Error, ChannelDetailWithMembers, ChannelDetailQueryKey>({
        queryKey,
        queryFn: async () => {
            const { data } = await applicationApi.getApplicationDetail(
                channelId,
                '1.1' // x-rwd-api-version
            );

            // API 응답 타입: ApplicationDetailResponseModelApiSuccessResponseModel
            // data.data가 ApplicationDetailResponseModel
            const response: ApplicationDetailResponseModel = data.data;
            
            // PlanType을 소문자로 변환 (예: 'Free' -> 'free')
            const planType = response?.planType?.toLowerCase() as Channel['plan'] | undefined;
            
            return {
                channelId: response?.applicationId ?? channelId,
                // applicationName을 channelName으로 매핑 (null이거나 undefined일 경우 빈 문자열)
                channelName: response?.applicationName ?? '',
                channelIntroduction: undefined, // ApplicationDetailResponseModel에 없음
                createdAt: response?.createdAt,
                contentCount: undefined, // ApplicationDetailResponseModel에 없음
                plan: planType,
                status: 'active', // ApplicationDetailResponseModel에 status 필드 없음, 기본값 사용
                members: response?.members, // 채널 유저 정보
            };
        },
        enabled: !!channelId,
        staleTime: staleTime ?? 30 * 1000,
        refetchOnMount: 'always',
        ...restQueryOptions,
    });
};

